import { useState, useEffect } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
const BookNow = () => {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: "",
    text: "",
  });

  useEffect(() => {
    GetData();
  }, []);
  const GetData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        "https://api.kurdishtranslationservices4u.co.uk/api/bookings"
      );

      setDataSource(
        res.data.data.map((row, index) => ({
          index: index + 1,
          id: row.id,
          full_name: row.full_name,
          phone_number: row.phone_number,
          email: row.email,
          country: row.country,
          subject: row.subject,
          instructions: row.instructions,
          files: row.files || [],
        }))
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  const columns = [
    { field: "index", headerName: "#", width: 50 },

    { field: "full_name", headerName: "Full Name", width: 200 },
    { field: "phone_number", headerName: "Phone Number", width: 150 },
    {
      field: "email",
      headerName: "Email",
      width: 250,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            py: 1,
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.5,
          }}
        >
          {params.value || "N/A"}
        </Box>
      ),
    },
    { field: "country", headerName: "Country", width: 150 },
    {
      field: "subject",
      headerName: "Subject",
      width: 140,
      renderCell: (params) => {
        const text = params.value || "N/A";
        const truncated =
          text.length > 50 ? text.substring(0, 50) + "..." : text;
        return (
          <Box
            onClick={() => {
              setModalContent({
                title: "Subject",
                text: text,
              });
              setModalOpen(true);
            }}
            sx={{
              width: "100%",
              py: 1,
              cursor: "pointer",
              color: "#2a5298",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            title="Click to view full text"
          >
            {truncated}
          </Box>
        );
      },
    },
    {
      field: "files",
      headerName: "Files",
      width: 90,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            py: 1,
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.5,
          }}
        >
          {params.value && params.value.length > 0 ? (
            params.value.map((file, index) => (
              <Box
                key={index}
                component="a"
                target="_blank"
                rel="noopener noreferrer"
                href={
                  "https://api.kurdishtranslationservices4u.co.uk/storage/" +
                  file.path
                }
                sx={{
                  display: "block",
                  color: "#2a5298",
                  textDecoration: "none",
                  mb: index < params.value.length - 1 ? 0.5 : 0,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                File {index + 1}
              </Box>
            ))
          ) : (
            <span style={{ color: "#999" }}>No files</span>
          )}
        </Box>
      ),
    },
    {
      field: "instructions",
      headerName: "Instructions",
      width: 300,
      renderCell: (params) => {
        const text = params.value || "N/A";
        const truncated =
          text.length > 60 ? text.substring(0, 60) + "..." : text;
        return (
          <Box
            onClick={() => {
              setModalContent({
                title: "Instructions",
                text: text,
              });
              setModalOpen(true);
            }}
            sx={{
              width: "100%",
              py: 1,
              cursor: "pointer",
              color: "#2a5298",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            title="Click to view full text"
          >
            {truncated}
          </Box>
        );
      },
    },
  ];

  const filteredRows = dataSource.filter((row) =>
    Object.values(row).some((val) =>
      String(val).toLowerCase().includes(searchText.toLowerCase())
    )
  );

  return (
    <div
      style={{
        margin: "25px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search"
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 250,
            maxWidth: 400,
            backgroundColor: "#f5f5f5",
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "#ddd" },
              "&:hover fieldset": { borderColor: "#999" },
              "&.Mui-focused fieldset": { borderColor: "#2a5298" },
            },
          }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: 300,
          }}
        >
          <CircularProgress size={60} sx={{ color: "#2a5298" }} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Book Now ...
          </Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            width: "100%",
            mb: 10,
            p: 2,
            overflowX: "auto",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          {/* Custom Pagination Controls - TOP */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              p: 1,
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" sx={{ mr: 2 }}>
                Rows per page:
              </Typography>
              <Select
                value={paginationModel.pageSize}
                onChange={(e) =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    pageSize: e.target.value,
                    page: 0,
                  }))
                }
                size="small"
                sx={{ height: "36px", minWidth: "80px" }}
              >
                {[10, 20, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Typography variant="body2">
              {`${
                paginationModel.page * paginationModel.pageSize + 1
              }-${Math.min(
                (paginationModel.page + 1) * paginationModel.pageSize,
                filteredRows.length
              )} of ${filteredRows.length}`}
            </Typography>

            <Box>
              <IconButton
                onClick={() =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: Math.max(prev.page - 1, 0),
                  }))
                }
                disabled={paginationModel.page === 0}
                size="small"
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                onClick={() =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: prev.page + 1,
                  }))
                }
                disabled={
                  (paginationModel.page + 1) * paginationModel.pageSize >=
                  filteredRows.length
                }
                size="small"
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>

          <DataGrid
            rows={filteredRows}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            autoHeight
            disableSelectionOnClick
            hideFooter={true}
            sx={{
              minWidth: 800,
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#2a5298",
                color: "white",
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: 16,
                  fontWeight: "bold",
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </Paper>
      )}

      {/* Modal for showing full text */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
            color: "white",
            fontWeight: 600,
          }}
        >
          {modalContent.title}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.8,
              color: "#333",
            }}
          >
            {modalContent.text}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setModalOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: "#2a5298",
              "&:hover": {
                backgroundColor: "#1e3c72",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BookNow;

// for(file in record.files){
//   <Image url=storageUrl+${file.name}></Image>
// }
